import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import RedeemIcon from "@mui/icons-material/Redeem";
import RuleIcon from "@mui/icons-material/Rule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { toast } from "react-toastify";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  getReferralSettings,
  resetReferralSettings,
  updateReferralSettings,
} from "services/referralService";
import ArgonInput from "components/ArgonInput";

const initialForm = {
  bonusAmount: "",
  minimumRedeemAmount: "",
  termsAndConditions: [""],
  isActive: true,
};

function ReferralSettings() {
  const [form, setForm] = useState(initialForm);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");

  const applySettingsToForm = (settings) => {
    setForm({
      bonusAmount: (settings?.bonusAmount ?? 0).toString(),
      minimumRedeemAmount: (settings?.minimumRedeemAmount ?? 0).toString(),
      termsAndConditions:
        settings?.termsAndConditions?.length > 0 ? settings.termsAndConditions : [""],
      isActive: Boolean(settings?.isActive),
    });
    setLastUpdated(settings?.updatedAt || "");
  };

  const fetchSettings = async () => {
    setFetchLoading(true);
    try {
      const response = await getReferralSettings();
      if (response.success) {
        applySettingsToForm(response.data);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch referral settings");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTermChange = (index, value) => {
    setForm((prev) => ({
      ...prev,
      termsAndConditions: prev.termsAndConditions.map((term, termIndex) =>
        termIndex === index ? value : term
      ),
    }));
  };

  const handleAddTerm = () => {
    setForm((prev) => ({
      ...prev,
      termsAndConditions: [...prev.termsAndConditions, ""],
    }));
  };

  const handleRemoveTerm = (index) => {
    setForm((prev) => ({
      ...prev,
      termsAndConditions:
        prev.termsAndConditions.length === 1
          ? [""]
          : prev.termsAndConditions.filter((_, termIndex) => termIndex !== index),
    }));
  };

  const validateForm = () => {
    const bonusAmount = Number(form.bonusAmount);
    const minimumRedeemAmount = Number(form.minimumRedeemAmount);

    if (Number.isNaN(bonusAmount) || bonusAmount < 0) {
      toast.error("Please enter a valid referral bonus amount.");
      return false;
    }

    if (Number.isNaN(minimumRedeemAmount) || minimumRedeemAmount < 0) {
      toast.error("Please enter a valid minimum redeem amount.");
      return false;
    }

    const terms = form.termsAndConditions.map((term) => term.trim()).filter(Boolean);
    if (!terms.length) {
      toast.error("Please add at least one term and condition.");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      bonusAmount: Number(form.bonusAmount),
      minimumRedeemAmount: Number(form.minimumRedeemAmount),
      termsAndConditions: form.termsAndConditions.map((term) => term.trim()).filter(Boolean),
      isActive: form.isActive,
    };

    setSaving(true);
    try {
      const response = await updateReferralSettings(payload);
      if (response.success) {
        applySettingsToForm(response.data);
        toast.success("Referral settings updated successfully!");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update referral settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await resetReferralSettings();
      if (response.success) {
        applySettingsToForm(response.data);
        toast.success("Referral settings reset to default!");
      }
    } catch (error) {
      toast.error(error.message || "Failed to reset referral settings");
    } finally {
      setResetting(false);
    }
  };

  const formattedUpdatedAt = lastUpdated ? new Date(lastUpdated).toLocaleString() : "Not saved yet";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox
              display="flex"
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              mb={3}
              pb={2}
              borderBottom="1px solid #e9ecef"
            >
              <ArgonBox display="flex" alignItems="center" gap={1.5}>
                <SettingsSuggestIcon color="primary" sx={{ fontSize: 32 }} />
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="medium">
                    Referral Settings
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text">
                    Last updated: {formattedUpdatedAt}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              {/* <ArgonBox display="flex" alignItems="center" gap={1.5}>
                {form.isActive ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <PauseCircleIcon color="warning" fontSize="small" />
                )}
                <ArgonTypography variant="button" fontWeight="medium">
                  {form.isActive ? "Active" : "Inactive"}
                </ArgonTypography>
                <Switch
                  checked={form.isActive}
                  onChange={(event) => handleChange("isActive", event.target.checked)}
                  disabled={fetchLoading || saving || resetting}
                />
              </ArgonBox> */}
            </ArgonBox>

            {fetchLoading ? (
              <ArgonBox textAlign="center" py={5}>
                <CircularProgress color="primary" />
                <ArgonTypography variant="body2" color="text" mt={2}>
                  Loading referral settings...
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                        <CurrencyRupeeIcon color="primary" fontSize="small" />
                        <ArgonTypography variant="caption" fontWeight="bold">
                          Referral Bonus Amount
                        </ArgonTypography>
                      </ArgonBox>
                      <TextField
                        fullWidth
                        type="number"
                        value={form.bonusAmount}
                        onChange={(event) => handleChange("bonusAmount", event.target.value)}
                        disabled={saving || resetting}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CurrencyRupeeIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </ArgonBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonBox mb={2}>
                      <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                        <RedeemIcon color="primary" fontSize="small" />
                        <ArgonTypography variant="caption" fontWeight="bold">
                          Minimum Redeem Amount
                        </ArgonTypography>
                      </ArgonBox>
                      <TextField
                        fullWidth
                        type="number"
                        value={form.minimumRedeemAmount}
                        onChange={(event) =>
                          handleChange("minimumRedeemAmount", event.target.value)
                        }
                        disabled={saving || resetting}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CurrencyRupeeIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </ArgonBox>
                  </Grid>
                </Grid>

                <ArgonBox mt={2}>
                  <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <RuleIcon color="primary" fontSize="small" />
                      <ArgonTypography variant="h6" fontWeight="medium">
                        Terms and Conditions
                      </ArgonTypography>
                    </ArgonBox>
                    <Tooltip title="Add term">
                      <span>
                        <IconButton
                          color="primary"
                          onClick={handleAddTerm}
                          disabled={saving || resetting}
                        >
                          <AddIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ArgonBox>

                  <ArgonBox display="flex" flexDirection="column" gap={1.5}>
                    {form.termsAndConditions.map((term, index) => (
                      <ArgonBox
                        key={`term-${index}`}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                      >
                        <ArgonInput
                          fullWidth
                          multiline
                          value={term}
                          onChange={(event) => handleTermChange(index, event.target.value)}
                          disabled={saving || resetting}
                          placeholder={`Term ${index + 1}`}
                        />
                        <Tooltip title="Remove term">
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveTerm(index)}
                              disabled={saving || resetting}
                              sx={{ mt: 0.5 }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ArgonBox>
                    ))}
                  </ArgonBox>
                </ArgonBox>

                <ArgonBox
                  display="flex"
                  justifyContent="flex-end"
                  flexWrap="wrap"
                  gap={1.5}
                  mt={4}
                  pt={3}
                  borderTop="1px solid #e9ecef"
                >
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleReset}
                    disabled={saving || resetting}
                  >
                    {resetting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <RestartAltIcon fontSize="small" />
                        Reset Default
                      </ArgonBox>
                    )}
                  </ArgonButton>
                  <ArgonButton variant="contained" color="primary" onClick={handleSave} disabled={saving || resetting}>
                    {saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <SaveIcon fontSize="small" />
                        Save Settings
                      </ArgonBox>
                    )}
                  </ArgonButton>
                </ArgonBox>
              </>
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ReferralSettings;